import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPlay, FiCheck, FiArrowRight, FiClock, FiDollarSign, FiZap, FiMessageCircle, FiStar, FiVideo, FiSmartphone, FiTarget, FiShoppingBag } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { submitVideoLead } from '../services/leadService';

const AdvertVideoMaker = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    business: '',
    goal: '',
    link: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Advert Video Maker | Professional Business Adverts | DigitalGigs";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitVideoLead(formData);
      setSubmitted(true);
      // Redirect to WhatsApp for immediate conversion
      setTimeout(() => {
        const message = encodeURIComponent(`Hi DigitalGigs, I just requested an advert video for ${formData.business}. My goal is: ${formData.goal}`);
        window.open(`https://wa.me/2348123456789?text=${message}`, '_blank');
      }, 1500);
    } catch (error) {
      console.error('Lead submission failed:', error);
      alert('There was an issue submitting your request. Please message us directly on WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const useCases = [
    { icon: FiShoppingBag, title: "Product Advertisements", desc: "Showcase physical products with high-impact visuals." },
    { icon: FiSmartphone, title: "Social Media Adverts", desc: "Stop the scroll on Facebook, Instagram, and TikTok." },
    { icon: FiTarget, title: "Promotional Videos", desc: "Professional business advert videos for services and events." },
    { icon: FiVideo, title: "Short Business Adverts", desc: "Quick 15-30 second clips optimized for mobile viewers." }
  ];

  return (
    <div className="bg-white selection:bg-emerald-100 selection:text-emerald-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-950">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px] -mr-48 -mt-48 opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight mb-8 leading-[1.1]">
                Advert Video Maker for Businesses That Need Attention
              </h1>
              <p className="text-xl text-slate-400 mb-10 leading-relaxed font-medium">
                Using a professional <span className="text-emerald-400">advert video maker</span> is the fastest way to grow your brand. Create professional advert videos for your products, services, and campaigns—without expensive video production.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-5">
                <a href="#get-started" className="w-full sm:w-auto flex items-center justify-center px-10 py-5 text-lg font-bold rounded-full text-slate-950 bg-emerald-400 hover:bg-emerald-300 transition-all shadow-2xl shadow-emerald-400/20">
                  Create My Advert Video <SafeIcon icon={FiArrowRight} className="ml-3" />
                </a>
                <a href="#examples" className="w-full sm:w-auto flex items-center justify-center px-10 py-5 text-lg font-bold rounded-full text-white border border-white/20 hover:bg-white/10 transition-all">
                  See Examples
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6 text-slate-500 font-bold text-sm">
                <span className="flex items-center gap-2"><SafeIcon icon={FiZap} className="text-emerald-400" /> Fast Production</span>
                <span className="flex items-center gap-2"><SafeIcon icon={FiStar} className="text-emerald-400" /> High ROI</span>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative aspect-video rounded-[2rem] overflow-hidden border border-white/10 bg-slate-900 shadow-2xl">
              <video 
                src="https://media-manager-c.questera.ai/greta-media/fafed1dc96bdeaff472e762fc9535f37237b73cd078ba929908d8fc9f91ee4faeb3717965c5074a04f18e80ee0d17e20/videos/dmlkZW8vbXA0/3113eb0665ee2139533009a1a6f32fcb.mp4" 
                autoPlay loop muted playsInline className="w-full h-full object-cover opacity-80" 
                alt="Professional business advert video maker example"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 bg-emerald-400/20 backdrop-blur-md rounded-full flex items-center justify-center text-emerald-400 text-3xl border border-emerald-400/30">
                  <SafeIcon icon={FiPlay} />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-16 max-w-4xl mx-auto">
            Need an Advert Video Without the Cost of a Production Team?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: FiDollarSign, title: "Avoid High Costs", desc: "Stop paying millions for production teams, cameras, and actors. Get the same result for less." },
              { icon: FiClock, title: "Save Your Time", desc: "Traditional production takes weeks. We deliver your ready-to-use advertising video in days." },
              { icon: FiZap, title: "Better Than Images", desc: "Static images are ignored. Our advert video maker creates content that stops the scroll." }
            ].map((item, i) => (
              <div key={i} className="p-10 bg-slate-50 rounded-[2.5rem] border border-slate-100 text-center hover:bg-white hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-emerald-600 mb-8 mx-auto shadow-sm">
                  <SafeIcon icon={item.icon} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-500 font-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section id="examples" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">The Only Advert Video Creator You Need</h2>
            <p className="text-slate-500 font-medium text-lg">We create high-performing content for every platform and objective.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((item, i) => (
              <div key={i} className="p-8 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow-md transition-all group">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 group-hover:text-white transition-all">
                  <SafeIcon icon={item.icon} className="text-xl" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-500 text-sm font-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-slate-950 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-emerald-400 font-black text-sm uppercase tracking-widest mb-4">Results First</h2>
            <p className="text-4xl md:text-5xl font-extrabold">Ready-to-use Advert Videos for Any Business</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Products', 'Restaurants', 'Real Estate', 'E-commerce', 'Events', 'Personal Brands', 'Local Businesses', 'Services'].map((niche, i) => (
              <div key={i} className="p-6 bg-white/5 border border-white/10 rounded-2xl flex items-center gap-3 hover:border-emerald-400/50 transition-all cursor-default">
                <SafeIcon icon={FiCheck} className="text-emerald-400" />
                <span className="font-bold">{niche}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900">3 Simple Steps to Your Advert</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { step: "01", title: "Send Your Info", desc: "Share your product details, images, or website link with us." },
              { step: "02", title: "We Create", desc: "Our specialists build your professional advertising video to match your brand." },
              { step: "03", title: "Ready to Use", desc: "Receive a high-quality, ready-to-use marketing video for your campaigns." }
            ].map((item, i) => (
              <div key={i} className="relative p-10 bg-white rounded-[2.5rem] border border-slate-100 shadow-sm text-center">
                <div className="text-6xl font-black text-slate-100 absolute top-4 left-1/2 -translate-x-1/2 z-0">{item.step}</div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-500 font-medium leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section id="get-started" className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[3rem] p-8 md:p-16 shadow-xl border border-slate-100">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Turn Your Product or Service Into an Advert People Can Watch</h2>
              <p className="text-slate-500 font-medium">Use the best <span className="text-emerald-600 font-bold">advert video maker</span> service to launch your next campaign.</p>
            </div>
            
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheck} className="text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Request Sent!</h3>
                <p className="text-slate-500 mb-8 font-medium">Opening WhatsApp to finalize your professional advert video details...</p>
                <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 1.5 }} className="h-full bg-emerald-500" />
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Full Name</label>
                    <input 
                      required
                      type="text" 
                      className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">WhatsApp Number</label>
                    <input 
                      required
                      type="tel" 
                      className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                      placeholder="e.g. 08123456789"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Business/Product Name</label>
                  <input 
                    required
                    type="text" 
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                    placeholder="What do you want advertised?"
                    value={formData.business}
                    onChange={(e) => setFormData({...formData, business: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">What is your advertising goal?</label>
                  <textarea 
                    required
                    rows="3"
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                    placeholder="e.g. Increase sales, get more followers, promote a new service..."
                    value={formData.goal}
                    onChange={(e) => setFormData({...formData, goal: e.target.value})}
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Website/Social Link (Optional)</label>
                  <input 
                    type="url" 
                    className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                    placeholder="https://instagram.com/yourbusiness"
                    value={formData.link}
                    onChange={(e) => setFormData({...formData, link: e.target.value})}
                  />
                </div>
                <button 
                  disabled={isSubmitting}
                  type="submit" 
                  className="w-full flex items-center justify-center px-10 py-5 text-xl font-bold rounded-[2rem] text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-600/20 active:scale-[0.98] disabled:opacity-50"
                >
                  {isSubmitting ? "Processing..." : "Create My Advert Video"} <SafeIcon icon={FiArrowRight} className="ml-3" />
                </button>
                <div className="flex items-center justify-center gap-2 pt-2">
                  <SafeIcon icon={FiMessageCircle} className="text-emerald-500" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Fastest response via WhatsApp</span>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 opacity-40 grayscale hover:grayscale-0 transition-all">
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black text-slate-900">500+</span>
              <span className="text-xs font-bold uppercase tracking-widest">Videos Produced</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black text-slate-900">48h</span>
              <span className="text-xs font-bold uppercase tracking-widest">Average Delivery</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black text-slate-900">100%</span>
              <span className="text-xs font-bold uppercase tracking-widest">Client Satisfaction</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-3xl font-black text-slate-900">24/7</span>
              <span className="text-xs font-bold uppercase tracking-widest">WhatsApp Support</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdvertVideoMaker;
